import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../App';

export default function Settings() {
  const { user, login, logout } = useAuth();
  const [googleStatus, setGoogleStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState('');
  const [syncDays, setSyncDays] = useState(90);
  const [members, setMembers] = useState([]);
  const [editMember, setEditMember] = useState(null);
  const [memberForm, setMemberForm] = useState({ name: '', color: '#6C63FF', avatar_emoji: '😊' });
  const [saving, setSaving] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState({
    email_morning: user?.email_morning !== 0,
    email_events: user?.email_events !== 0,
    email_tasks: user?.email_tasks !== 0,
    email_reminders: user?.email_reminders !== 0,
  });
  const [prefsSaved, setPrefsSaved] = useState(false);
  const [emailAccount, setEmailAccount] = useState(null);
  const [emailForm, setEmailForm] = useState({ imap_host: 'imap.orange.fr', imap_port: 993, imap_user: '', imap_pass: '' });
  const [savingEmail, setSavingEmail] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailResult, setEmailResult] = useState('');

  const COLORS = ['#6C63FF','#2196F3','#E91E63','#4CAF50','#FF9800','#9C27B0','#00BCD4','#F44336','#795548','#607D8B'];
  const AVATARS = ['😊','👨','👩','👦','👧','🧑','👴','👵','🦸','🎅','🧑‍🍳','👩‍🎓','👨‍💼','🧑‍🏫','🐶','🐱'];

  useEffect(() => {
    api.get('/google/status').then(r => setGoogleStatus(r.data)).catch(() => {});
    api.get('/family/members').then(r => setMembers(r.data)).catch(() => {});
    api.get('/email/account').then(r => setEmailAccount(r.data)).catch(() => {});

    // Détecter retour OAuth Google
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_synced')) {
      setSyncResult('✅ Google Calendar connecté !');
      setGoogleStatus({ connected: true });
      window.history.replaceState({}, '', '/parametres');
    }
    if (params.get('google_error')) {
      setSyncResult('❌ Erreur de connexion Google');
      window.history.replaceState({}, '', '/parametres');
    }
    // Détecter retour OAuth Microsoft
    if (params.get('email_ok')) {
      setEmailResult('✅ Boîte Outlook connectée avec succès !');
      api.get('/email/account').then(r => setEmailAccount(r.data)).catch(() => {});
      window.history.replaceState({}, '', '/parametres');
    }
    if (params.get('email_error')) {
      setEmailResult('❌ Erreur : ' + decodeURIComponent(params.get('email_error')));
      window.history.replaceState({}, '', '/parametres');
    }
  }, []);

  const connectGoogle = async () => {
    try {
      const { data } = await api.get('/google/auth-url');
      window.location.href = data.url;
    } catch (e) {
      setSyncResult('❌ ' + (e.response?.data?.error || e.message));
    }
  };

  const syncGoogle = async () => {
    setSyncing(true);
    setSyncResult('');
    try {
      const { data } = await api.post('/google/sync', { days: syncDays });
      setSyncResult(`✅ ${data.imported} événements importés (${data.skipped} déjà présents sur ${data.total} trouvés)`);
    } catch (e) {
      setSyncResult('❌ ' + (e.response?.data?.error || e.message));
    }
    setSyncing(false);
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Déconnecter Google Calendar ?')) return;
    await api.delete('/google/disconnect');
    setGoogleStatus({ connected: false });
    setSyncResult('Google Calendar déconnecté.');
  };

  const saveEmailPrefs = async (newPrefs) => {
    try {
      const updated = await api.put('/users/me/email-prefs', newPrefs);
      const token = localStorage.getItem('family_token');
      login(token, { ...user, ...updated.data });
      setPrefsSaved(true);
      setTimeout(() => setPrefsSaved(false), 2000);
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const saveEmailAccount = async () => {
    setSavingEmail(true); setEmailResult('');
    try {
      await api.post('/email/account', emailForm);
      api.get('/email/account').then(r => setEmailAccount(r.data));
      setShowEmailForm(false);
      setEmailResult('✅ Boîte email configurée ! Sync automatique toutes les 30 min.');
      setTimeout(() => setEmailResult(''), 4000);
    } catch (e) { setEmailResult('❌ ' + (e.response?.data?.error || e.message)); }
    setSavingEmail(false);
  };

  const connectMicrosoft = async () => {
    try {
      const { data } = await api.get('/email/oauth/start');
      window.location.href = data.url;
    } catch (e) { setEmailResult('❌ ' + (e.response?.data?.error || e.message)); }
  };

  const disconnectEmailAccount = async () => {
    if (!window.confirm('Déconnecter cette boîte email ?')) return;
    await api.delete('/email/account');
    setEmailAccount(null);
  };

  const togglePref = (key) => {
    const newPrefs = { ...emailPrefs, [key]: !emailPrefs[key] };
    setEmailPrefs(newPrefs);
    saveEmailPrefs(newPrefs);
  };

  const openEditMember = (m) => {
    setMemberForm({ name: m.name, color: m.color, avatar_emoji: m.avatar_emoji });
    setEditMember(m);
  };

  const saveMember = async () => {
    setSaving(true);
    try {
      await api.put(`/family/members/${editMember.id}`, memberForm);
      setEditMember(null);
      api.get('/family/members').then(r => setMembers(r.data));
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setSaving(false);
  };

  const uploadMemberAvatar = async (memberId, file) => {
    const fd = new FormData();
    fd.append('avatar', file);
    try {
      await api.post(`/family/members/${memberId}/avatar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      api.get('/family/members').then(r => setMembers(r.data));
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const renderAvatar = (member, size = 40) => {
    if (member?.avatar_url) {
      return <img src={member.avatar_url} alt={member.name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${member.color}` }} />;
    }
    return <div style={{ width: size, height: size, borderRadius: '50%', background: (member?.color || '#6C63FF') + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.55 }}>{member?.avatar_emoji || '😊'}</div>;
  };

  return (
    <div>
      <div className="topbar">
        <div className="page-title">⚙️ Paramètres</div>
      </div>

      <div className="page" style={{ paddingTop: 0 }}>

        {/* MEMBRES FAMILLE */}
        <div className="section-title">👨‍👩‍👧 Membres de la famille</div>
        {members.map(m => (
          <div key={m.id} className="card-sm" style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => openEditMember(m)}>
            {renderAvatar(m, 40)}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{m.name}</div>
              <div className="text-muted">{m.email}</div>
            </div>
            <div style={{ width: 16, height: 16, borderRadius: '50%', background: m.color }} />
            <span style={{ color: '#BBBBCC', fontSize: 18 }}>›</span>
          </div>
        ))}

        {/* BOÎTE EMAIL */}
        <div className="section-title" style={{ marginTop: 24 }}>📧 Boîte email</div>
        <div className="card">
          {emailResult && (
            <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 10, background: emailResult.startsWith('✅') ? '#E8F5E9' : '#FFEBEE', color: emailResult.startsWith('✅') ? '#2E7D32' : '#C62828', fontSize: 13, fontWeight: 600 }}>
              {emailResult}
            </div>
          )}
          {emailAccount ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: '#E3F2FD', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>📬</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{emailAccount.imap_user}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    ✅ Connectée · {emailAccount.imap_host}
                    {emailAccount.last_sync && ` · Sync ${new Date(emailAccount.last_sync).toLocaleDateString('fr-FR')}`}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => window.location.href = '/emails'}>📬 Voir les emails</button>
                <button className="btn btn-ghost btn-sm" style={{ color: '#EF5350' }} onClick={disconnectEmailAccount}>Déconnecter</button>
              </div>
            </>
          ) : showEmailForm ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Connexion boîte email</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 8, marginBottom: 8 }}>
                <div className="form-group">
                  <label className="form-label">Serveur IMAP</label>
                  <select className="form-input" value={emailForm.imap_host} onChange={e => setEmailForm({ ...emailForm, imap_host: e.target.value })}>
                    <option value="imap.orange.fr">Orange (imap.orange.fr)</option>
                    <option value="imap.gmail.com">Gmail (imap.gmail.com)</option>
                    <option value="imap-mail.outlook.com">Outlook/Hotmail</option>
                    <option value="imap.free.fr">Free (imap.free.fr)</option>
                    <option value="imap.sfr.fr">SFR (imap.sfr.fr)</option>
                    <option value="custom">Autre...</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Port</label>
                  <input className="form-input" type="number" value={emailForm.imap_port} onChange={e => setEmailForm({ ...emailForm, imap_port: Number(e.target.value) })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Adresse email</label>
                <input className="form-input" type="email" placeholder="votre@orange.fr" value={emailForm.imap_user} onChange={e => setEmailForm({ ...emailForm, imap_user: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Mot de passe</label>
                <input className="form-input" type="password" placeholder="Mot de passe email" value={emailForm.imap_pass} onChange={e => setEmailForm({ ...emailForm, imap_pass: e.target.value })} />
              </div>
              <div style={{ background: '#FFF8E1', borderRadius: 8, padding: '8px 12px', fontSize: 11, color: '#E65100', marginBottom: 8 }}>
                🔒 Votre mot de passe est chiffré (AES-256) avant d'être stocké sur le serveur.
              </div>
              {emailForm.imap_host === 'imap-mail.outlook.com' && (
                <div style={{ background: '#E3F2FD', borderRadius: 8, padding: '10px 12px', fontSize: 11, color: '#1565C0', marginBottom: 12, lineHeight: 1.6 }}>
                  <strong>📋 Outlook/Hotmail — À faire avant de connecter :</strong><br />
                  1. Aller sur <strong>outlook.com</strong> → Paramètres ⚙️ → Afficher tous les paramètres → Courrier → Synchronisation → Activer IMAP<br />
                  2. Si double authentification activée : créer un <strong>mot de passe d'application</strong> sur account.microsoft.com/security → Sécurité → Mot de passe d'application, et utiliser ce code ici.
                </div>
              )}
              {emailForm.imap_host === 'imap.gmail.com' && (
                <div style={{ background: '#E8F5E9', borderRadius: 8, padding: '10px 12px', fontSize: 11, color: '#2E7D32', marginBottom: 12, lineHeight: 1.6 }}>
                  <strong>📋 Gmail — Mot de passe d'application requis :</strong><br />
                  1. Aller sur <strong>myaccount.google.com</strong> → Sécurité → Mots de passe des applications<br />
                  2. Créer un mot de passe pour "FamilyOS" → copier le code 16 caractères et l'utiliser ici (pas votre vrai mot de passe Google).
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost" onClick={() => setShowEmailForm(false)} style={{ flex: 1 }}>Annuler</button>
                <button className="btn btn-primary" onClick={saveEmailAccount} disabled={savingEmail || !emailForm.imap_user || !emailForm.imap_pass} style={{ flex: 2 }}>
                  {savingEmail ? <span className="spinner" style={{ width: 16, height: 16 }} /> : '🔗 Connecter'}
                </button>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button className="btn btn-primary" style={{ width: '100%', background: '#0078D4', borderColor: '#0078D4', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} onClick={connectMicrosoft}>
                <span style={{ fontSize: 16 }}>🪟</span> Connecter Outlook / Hotmail
              </button>
              <button className="btn btn-ghost" style={{ width: '100%' }} onClick={() => setShowEmailForm(true)}>
                📧 Autre boîte (Orange, Gmail, Free...)
              </button>
            </div>
          )}
        </div>

        {/* GOOGLE CALENDAR */}
        <div className="section-title" style={{ marginTop: 24 }}>📅 Google Calendar</div>
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#E3F2FD', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>📅</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>Google Calendar</div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                {googleStatus?.connected
                  ? `✅ Connecté${googleStatus.updated_at ? ` · ${new Date(googleStatus.updated_at).toLocaleDateString('fr-FR')}` : ''}`
                  : '⚪ Non connecté'}
              </div>
            </div>
          </div>

          {!googleStatus?.connected ? (
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={connectGoogle}>
              🔗 Connecter Google Calendar
            </button>
          ) : (
            <>
              <div style={{ marginBottom: 12 }}>
                <label className="form-label">Importer les événements des</label>
                <select className="form-input" value={syncDays} onChange={e => setSyncDays(Number(e.target.value))}>
                  <option value={30}>30 prochains jours</option>
                  <option value={90}>90 prochains jours</option>
                  <option value={180}>6 prochains mois</option>
                  <option value={365}>12 prochains mois</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={{ flex: 2 }} onClick={syncGoogle} disabled={syncing}>
                  {syncing ? <><span className="spinner" style={{ width: 14, height: 14, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Import...</> : '↓ Importer les événements'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={disconnectGoogle} style={{ color: '#EF5350' }}>Déconnecter</button>
              </div>
            </>
          )}

          {syncResult && (
            <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 10, background: syncResult.startsWith('✅') ? '#E8F5E9' : '#FFEBEE', color: syncResult.startsWith('✅') ? '#2E7D32' : '#C62828', fontSize: 13, fontWeight: 600 }}>
              {syncResult}
            </div>
          )}

          <div style={{ marginTop: 14, padding: '12px 14px', background: '#FFF8E1', borderRadius: 10, fontSize: 12, color: '#E65100', lineHeight: 1.6 }}>
            <strong>ℹ️ Comment configurer :</strong><br />
            1. <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" style={{ color: '#6C63FF' }}>console.cloud.google.com</a> → Créer un projet "FamilyOS"<br />
            2. APIs & Services → Bibliothèque → "Google Calendar API" → Activer<br />
            3. Identifiants → Créer → ID client OAuth 2.0 → Application Web<br />
            4. URI de redirection : <code style={{ background: '#fff', padding: '1px 6px', borderRadius: 4 }}>https://onyx.ifclubs.fr/api/google/callback</code><br />
            5. Copier Client ID + Client Secret → les mettre dans le fichier <code>.env</code> du serveur
          </div>
        </div>

        {/* COMPTE + PRÉFÉRENCES EMAIL */}
        <div className="section-title" style={{ marginTop: 24 }}>👤 Mon compte</div>
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            {user.avatar_url
              ? <img src={user.avatar_url} alt={user.name} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${user.color}` }} />
              : <div style={{ width: 48, height: 48, borderRadius: '50%', background: user.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>{user.avatar_emoji}</div>
            }
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{user.name}</div>
              <div className="text-muted">{user.email} · {user.role}</div>
            </div>
          </div>

          <div style={{ borderTop: '1px solid #F0F2FF', paddingTop: 14, marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#555577', marginBottom: 10 }}>
              📧 Emails reçus
              {prefsSaved && <span style={{ marginLeft: 8, fontSize: 11, color: '#4CAF50', fontWeight: 600 }}>✓ Sauvegardé</span>}
            </div>
            {[
              { key: 'email_morning', icon: '🌅', label: 'Résumé matinal (7h30)', desc: 'Tâches, repas, agenda du jour' },
              { key: 'email_events', icon: '📅', label: 'Rappels événements', desc: '24h et 2h avant chaque événement' },
              { key: 'email_tasks', icon: '✅', label: 'Nouvelles tâches assignées', desc: 'Quand une tâche vous est assignée' },
              { key: 'email_reminders', icon: '🔔', label: 'Rappels ponctuels', desc: 'À l\'heure exacte du rappel' },
            ].map(({ key, icon, label, desc }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #F5F6FF' }}>
                <span style={{ fontSize: 20 }}>{icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#333355' }}>{label}</div>
                  <div style={{ fontSize: 11, color: '#AAAACC' }}>{desc}</div>
                </div>
                <button onClick={() => togglePref(key)}
                  style={{ width: 46, height: 26, borderRadius: 13, background: emailPrefs[key] ? '#6C63FF' : '#E0E4F0', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'white', position: 'absolute', top: 3, left: emailPrefs[key] ? 23 : 3, transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
                </button>
              </div>
            ))}
            <div style={{ marginTop: 10, fontSize: 11, color: '#AAAACC', lineHeight: 1.5 }}>
              Les emails sont envoyés à : <strong>{user.email}</strong>
            </div>
          </div>

          <button className="btn btn-danger" style={{ width: '100%' }} onClick={logout}>⎋ Se déconnecter</button>
        </div>

        <div style={{ height: 8 }} />
      </div>

      {/* MODAL EDIT MEMBRE */}
      {editMember && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditMember(null)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">✏️ Modifier {editMember.name}</div>

            {/* Photo de profil */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18, background: '#F8F9FF', borderRadius: 14, padding: 14 }}>
              {renderAvatar(editMember, 72)}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Photo de profil</div>
                <label style={{ display: 'inline-block', background: '#6C63FF', color: 'white', borderRadius: 10, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  📷 Choisir une photo
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files[0] && uploadMemberAvatar(editMember.id, e.target.files[0])} />
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Prénom</label>
              <input className="form-input" value={memberForm.name} onChange={e => setMemberForm({ ...memberForm, name: e.target.value })} />
            </div>

            <div className="form-group">
              <label className="form-label">Emoji (si pas de photo)</label>
              <div className="emoji-grid">
                {AVATARS.map(em => (
                  <button key={em} type="button" className={`emoji-btn${memberForm.avatar_emoji === em ? ' selected' : ''}`} onClick={() => setMemberForm({ ...memberForm, avatar_emoji: em })}>{em}</button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Couleur</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setMemberForm({ ...memberForm, color: c })}
                    style={{ width: 32, height: 32, borderRadius: '50%', background: c, border: memberForm.color === c ? '3px solid white' : '3px solid transparent', outline: memberForm.color === c ? `3px solid ${c}` : 'none', cursor: 'pointer' }} />
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setEditMember(null)} style={{ flex: 1 }}>Annuler</button>
              <button className="btn btn-primary" onClick={saveMember} disabled={saving} style={{ flex: 2 }}>
                {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
